import { Component, OnInit } from '@angular/core';
import { TrpcService } from '../../services/trpc/trpc.service';
import { MessageService, ConfirmationService } from 'primeng/api';

interface InvoicePayloadFile {
  fileName: string;
  filePath: string;
  relativePath: string;
  locationNumber: string;
  suite: string;
  account: string;
  timestamp: string;
  fileSize: number;
  modifiedDate: string;
}

@Component({
  selector: 'app-dev-invoice-generation',
  templateUrl: './dev-invoice-generation.component.html',
  styleUrl: './dev-invoice-generation.component.scss',
  providers: [ConfirmationService]
})
export class DevInvoiceGenerationComponent implements OnInit {
  files: InvoicePayloadFile[] = [];
  loading: boolean = false;
  creating: boolean = false;
  selectedFile: InvoicePayloadFile | null = null;
  currentLocationId: string = '';

  constructor(
    private trpcService: TrpcService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  async ngOnInit() {
    // Subscribe to current location changes
    this.trpcService.currentLocationObservable.subscribe(locationId => {
      this.currentLocationId = locationId;
      console.log(`Current location ID: ${locationId}`);
    });

    await this.loadFiles();
  }

  // Filter files to only show those for the current location
  get filteredFiles(): InvoicePayloadFile[] {
    if (!this.currentLocationId) {
      // If no location is set, show all files
      console.log('No location ID set, showing all files');
      return this.files;
    }
    const filtered = this.files.filter(file => file.locationNumber === this.currentLocationId);
    console.log(`Filtering files: currentLocationId="${this.currentLocationId}", total files=${this.files.length}, filtered=${filtered.length}`);
    if (this.files.length > 0 && filtered.length === 0) {
      console.warn('No files match current location. Available location numbers:',
        [...new Set(this.files.map(f => f.locationNumber))]);
    }
    return filtered;
  }

  async loadFiles() {
    this.loading = true;
    try {
      this.files = await this.trpcService.trpc.dev.listInvoicePayloadFiles.query();
      console.log(`Loaded ${this.files.length} total invoice payload files (${this.filteredFiles.length} for current location)`);
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to load invoice payload files: ${error.message || 'Unknown error'}`
      });
      console.error('Error loading invoice payload files:', error);
    } finally {
      this.loading = false;
    }
  }

  async refreshFiles() {
    await this.loadFiles();
    this.messageService.add({
      severity: 'success',
      summary: 'Refreshed',
      detail: 'File list refreshed successfully'
    });
  }

  confirmCreateInvoice(file: InvoicePayloadFile) {
    this.selectedFile = file;

    this.confirmationService.confirm({
      message: `Create invoice for location ${file.locationNumber}, suite ${file.suite}, account ${file.account}?`,
      header: 'Confirm Invoice Creation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Create Invoice',
      rejectLabel: 'Cancel',
      accept: () => {
        this.createInvoice(file);
      }
    });
  }

  async createInvoice(file: InvoicePayloadFile) {
    if (this.creating) {
      return;
    }

    this.creating = true;
    try {
      console.log(`Creating invoice from file: ${file.filePath}`);

      const result = await this.trpcService.trpc.invoice.createInvoiceFromJsonFile.mutate({
        filePath: file.filePath,
        lid: file.locationNumber
      });

      if (result.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Invoice Created',
          detail: `Invoice #${(result as any).invoiceId} created successfully for location ${file.locationNumber}`
        });
        console.log('Invoice creation result:', result);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Invoice Creation Failed',
          detail: result.message || 'Unknown error'
        });
      }
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to create invoice: ${error.message || 'Unknown error'}`
      });
      console.error('Error creating invoice:', error);
    } finally {
      this.creating = false;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  formatTimestamp(timestamp: string): string {
    if (!timestamp || timestamp.length < 8) return timestamp;
    // Format: 20250129 -> 2025-01-29
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    return `${year}-${month}-${day}`;
  }
}
